import { Grid, IconButton, useEventCallback } from '@mui/material';
import { BewareFormVM, TableBewareVM } from 'mutation/view-models/registration-beware';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { SubjectsBeware } from 'shell/models/person/subjects-beware';
import { DateTime } from 'luxon';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells, headCellsWithoutAction } from './constants';
import { Generality } from 'shell/models/person/generality';
import { Delete, Visibility } from '@mui/icons-material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import ModalEditBeware from './modal-edit-beware';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { bewares } from '../typology-beware-list';

interface TableBewareProps {
  generality?: Generality;
  handleChangePage: (page: number) => void;
  handleChangeRowsPerPage: (rowsPerPage: number) => void;
  page?: number;
  rowsPerPage?: number;
  setWithBeware?: (value: boolean) => void;
  withoutAction?: boolean;
}

const TableBewareComponent = ({
  generality,
  handleChangePage,
  handleChangeRowsPerPage,
  page,
  rowsPerPage,
  setWithBeware,
  withoutAction = false,
}: TableBewareProps): React.ReactElement => {
  const { userProfile } = useUserProfile();

  const [bodyBeware, getBodyBeware] = useHttpFetch<
    HttpResponseList<SubjectsBeware>,
    Array<TableBewareVM>
  >(`${__PERSON_NEW_API_URL__}/persona/diffide/ricerca`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      return response.data.payload?.list?.map((item) => {
        const from = item.key?.dataInizio ? DateTime.fromISO(item.key?.dataInizio) : '';
        const to = item.dataFine ? DateTime.fromISO(item.dataFine) : '';
        const year = from && to ? (to.year - from.year)?.toString() : '';

        const type = bewares.find((beware) => beware.id === item.tipoDiffida);

        return {
          type: type?.value,
          from: from ? from.toFormat('dd/MM/yyyy') : '',
          to: to ? to.toFormat('dd/MM/yyyy') : '',
          note: item.note || '',
          // per modifica
          typology: item.key?.tipo || '',
          // italian
          municipalityItalian: {
            description: item.nomeComune || '',
            province: item.provincia || '',
            id: item.key?.comune || '',
          },
          year,
          key: item.key,
        } as TableBewareVM;
      }) as Array<TableBewareVM>;
    },
  });

  const [bodyValue, setBodyValue] = useState<Array<TableBewareVM>>([]);
  const [openModalEdit, setOpenModalEdit] = useState<{
    open: boolean;
    initialValue?: BewareFormVM;
    index?: number;
  }>({ open: false, initialValue: undefined, index: undefined });

  useEffect(() => {
    if (generality && bodyBeware.pristine && !bodyBeware.isLoading) {
      getBodyBeware({ data: { ...generality } });
    }
  }, [generality, bodyBeware]);

  const deleteData = useHttpClient(`${__PERSON_API_URL__}/persona/diffide/elimina`, {
    options: {
      method: 'DELETE',
    },
  });

  const onDeletePress = useEventCallback((item: TableBewareVM, index: number) => {
    (
      deleteData({
        data: {
          ...item.key,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');

      const newBody = bodyValue.slice().filter((_, indexCurr) => indexCurr !== index);

      setBodyValue(newBody);
    });
  });

  const onVisibilityPress = useEventCallback((item: TableBewareVM, index: number) => {
    const initialValue: BewareFormVM = {
      typology: item.typology || '',
      municipalityItalian: item.municipalityItalian || null,
      dateForm: item.from ? DateTime.fromFormat(item.from, 'dd/MM/yyyy') : null,
      year: item.year,
      note: item.note,
      key: item.key,
    };

    setOpenModalEdit({ open: true, initialValue, index });
  });

  const body: Array<TableBewareVM> = useMemo(() => {
    if (generality) {
      const res: Array<TableBewareVM> = bodyBeware.data as Array<TableBewareVM>;

      return res?.map((item, index) => ({
        ...item,
        action: withoutAction ? undefined : (
          <>
            {userProfile.datiLogin?.profile?.feature?.diffida === '0' ? undefined : (
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onVisibilityPress(item, index);
                  }}
                >
                  <Visibility />
                </IconButton>
              </span>
            )}
            {userProfile.datiLogin?.profile?.feature?.diffida === '2' ? (
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeletePress(item, index);
                  }}
                  color="cdmBrand"
                >
                  <Delete />
                </IconButton>
              </span>
            ) : undefined}
          </>
        ),
      }));
    }
    return [];
  }, [
    bodyBeware.data,
    generality,
    withoutAction,
    userProfile.datiLogin?.profile?.feature?.diffida,
    onDeletePress,
    onVisibilityPress,
  ]);

  useEffect(() => {
    if (bodyBeware.isLoading || bodyBeware.pristine) {
      return;
    }

    if (setWithBeware && ((bodyBeware.data as Array<TableBewareVM>) || []).length > 0) {
      setWithBeware(true);
    }
    if (setWithBeware && !((bodyBeware.data as Array<TableBewareVM>) || []).length) {
      setWithBeware(false);
    }
  }, [bodyBeware.isLoading, bodyBeware.pristine, bodyBeware.data]);

  useEffect(() => {
    if (body?.length > 0) {
      setBodyValue(body);
    }
  }, [body]);

  const onCloseModal = useEventCallback((isEdit?: boolean) => {
    if (isEdit) {
      getBodyBeware({ data: { ...generality } });
    }

    setOpenModalEdit({ open: false, initialValue: undefined, index: undefined });
  });

  return (
    <Fragment>
      <Grid item xs={12}>
        {((bodyValue as Array<TableBewareVM>) || []).length ? (
          <Table
            onSort={noop}
            head={withoutAction ? headCellsWithoutAction : headCells}
            orders={{}}
            page={page || 0}
            rowsPerPage={rowsPerPage}
            body={bodyValue as Array<TableBewareVM>}
            count={((bodyValue as Array<TableBewareVM>) || []).length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            hideButtons
            noSort
            ariaLabel="Lista diffida"
          />
        ) : undefined}
      </Grid>
      <ModalEditBeware
        open={openModalEdit.open}
        initialValues={openModalEdit.initialValue}
        onClose={onCloseModal}
      />
    </Fragment>
  );
};

TableBewareComponent.displayName = 'Accordion Subject Attributes - Form - Table component';

const TableBeware = memo(TableBewareComponent);

export default TableBeware as typeof TableBewareComponent;
